import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import FireIconSvg from '../../images/createFromTemplate/FireIcon.svg';
import { useRightPanelVisible } from '../../../RightPanel/useRightPanelVisible.ts';

import { TemplateCard, WhiteboardCard } from './TemplateCard.tsx';

const WHITEBOARD = 'whiteboard';

const i18n = defineMessages({
	popularFor: {
		id: 'home.views.create-from-templates.templates-list.popular-for',
		defaultMessage: 'Popular for {teamType}',
		description: 'Secondary text for the 1st template when we have team type',
	},
	popular: {
		id: 'home.views.create-from-templates.templates-list.popular',
		defaultMessage: 'Popular',
		description: 'Secondary text for the 1st template',
	},
	recommendedFor: {
		id: 'home.views.create-from-templates.templates-list.recommended-for',
		defaultMessage: 'Recommended for {teamType}',
		description: 'Secondary text for the subsequent templates when we have team type',
	},
	recommended: {
		id: 'home.views.create-from-templates.templates-list.recommended',
		defaultMessage: 'Recommended',
		description: 'Secondary text for the subsequent templates',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GridContainer = styled.ul<{ shouldShowRightPanel: boolean }>({
	display: 'grid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	gridTemplateColumns: ({ shouldShowRightPanel }) =>
		!shouldShowRightPanel
			? 'repeat(calc(3 - var(--homeIsSingleColumn)), minmax(0, 1fr))'
			: 'repeat(2, minmax(0, 1fr))',
	gridTemplateRows: 'repeat(calc(2 + var(--homeIsSingleColumn), auto))',
	gap: token('space.200'),
	marginBottom: token('space.200'),
	listStyleType: 'none',
	paddingLeft: 0,
	margin: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SecondaryContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiContainer = styled.div({
	marginRight: token('space.075'),
	display: 'flex',
	alignItems: 'center',
});

export type TemplatesListProps = {
	teamType: string;
	teamTypeLoading: boolean;
	templateKeys: string[];
	personalSpaceKey: string;
	onRenderingDone: any;
};

export const TemplatesList: FC<TemplatesListProps> = ({
	teamType,
	teamTypeLoading,
	templateKeys,
	personalSpaceKey,
	onRenderingDone,
}) => {
	const { shouldShowRightPanel } = useRightPanelVisible();

	const determineSecondary = (first: boolean, teamType: string) => {
		if (first) {
			return (
				<SecondaryContainer>
					<EmojiContainer>
						<img src={FireIconSvg} alt="" />
					</EmojiContainer>
					{teamType ? (
						<Text>
							<FormattedMessage
								{...i18n.popularFor}
								values={{
									teamType,
								}}
							/>
						</Text>
					) : (
						<FormattedMessage {...i18n.popular} />
					)}
				</SecondaryContainer>
			);
		}

		return (
			<SecondaryContainer>
				{teamType ? (
					<FormattedMessage
						{...i18n.recommendedFor}
						values={{
							teamType,
						}}
					/>
				) : (
					<FormattedMessage {...i18n.recommended} />
				)}
			</SecondaryContainer>
		);
	};

	return (
		<GridContainer shouldShowRightPanel={shouldShowRightPanel}>
			{templateKeys.map((templateKey, idx) => {
				const secondary = determineSecondary(idx === 0, teamType);
				const Card = templateKey === WHITEBOARD ? WhiteboardCard : TemplateCard;
				return (
					<Card
						key={templateKey}
						templateKey={templateKey}
						teamTypeLoading={teamTypeLoading}
						secondary={secondary}
						personalSpaceKey={personalSpaceKey}
						onRenderingDone={onRenderingDone}
					/>
				);
			})}
		</GridContainer>
	);
};
